import { Badge } from '../../Utility/Badge';
import { Button } from '../../Utility/Button';
import { AssistantTabs, DLPState } from '../../../Types/Enums';
import { Prompt as PromptType } from '../../../Types';
import { ArrowLeft, ArrowCounterClockwise } from '@phosphor-icons/react';
import AdminBox from '../../Utility/AdminBox';
import AdminBoxHeadline from '../../Utility/AdminBoxHeadline';
import AdminContainer from '../../Utility/AdminContainer';
import Chat from '../../Chat/Chat';
import ImageUpload from '../../Utility/ImageUpload';
import Input from '../../Utility/FormElements/Input';
import React, { useEffect, useMemo, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import usePrompt from '../../../Hooks/usePrompt';
import ModelTab from './components/ModelTab';
import ConnectorTab from './components/ConnectorTab';
import FunctionsTab from './components/FunctionsTab';
import DescriptionTab from './components/DescriptionTab';
import KnowledgebaseTab from './components/KnowledgebaseTab';
import DataProtectionTab from './components/DataProtectionTab';
import TabSection from './components/TabSection';
import { modelService } from '../../../Services/modelService';
import ResetButton from './components/ResetButton';
import ConfigBox from './components/ConfigBox';
import GroupTab from './components/GroupTab';
import InfoButton from '../../Utility/InfoButton';
import { ArrowDown } from '@phosphor-icons/react/dist/ssr';
import useGlobals from '../../../Hooks/useGlobals';

function Prompt(props: { userPrompt: boolean }) {
    const { demo, settings } = useGlobals();
    const {
        PromptActions,
        boxHeightClassesStr,
        chatId,
        filesChangedModalOpen,
        filesInputKey,
        i18n,
        imagePreview,
        isAssistantModel,
        lastClickedElementRef,
        loadedStates,
        models,
        prompt,
        promptFunctions,
        promptIdentifier,
        promptSnapshot,
        setIsAssistantModel,
        userPrompt,
        knowledgebaseTotal,
        selectedTab,
        hasChanges,
        changedSections,
        bestPracticeSystemPrompt,
        customButtonArray,
        isSaving,
        groups,
        systemPromptInputRef,
    } = usePrompt(props.userPrompt);

    const handlePromptUpdate = (updates: Partial<PromptType>) => {
        PromptActions.updatePrompt(updates);
    };

    const [localCombinedDlp, setLocalCombinedDlp] = useState<DLPState>(DLPState.dlp_optional);
    const [modelName, setModelName] = useState<string>('Nicht ausgewählt');
    const [isTextareaFocused, setIsTextareaFocused] = useState<boolean>(false);
    const [allowedMimeTypes, setAllowedMimeTypes] = useState<string[]>([]);
    useEffect(() => {
        if (prompt.model) {
            modelService.getModel(prompt.model).then((response) => {
                setModelName(response.data.title || prompt.model);
                setAllowedMimeTypes(response.data.allowedMimeTypes);
            });
        } else {
            setModelName('Nicht ausgewählt');
        }
    }, [prompt.model]);

    useEffect(() => {
        switch (prompt.dlp) {
            case DLPState.dlp_on:
                setLocalCombinedDlp(DLPState.dlp_on);
                break;
            case DLPState.dlp_optional:
                setLocalCombinedDlp(prompt.modelDlp === DLPState.dlp_on ? DLPState.dlp_on : DLPState.dlp_optional);
                break;
            case DLPState.dlp_off:
                setLocalCombinedDlp(prompt.modelDlp === DLPState.dlp_off ? DLPState.dlp_off : prompt.modelDlp);
                break;
        }
    }, [prompt.dlp, prompt.modelDlp]);

    const chatElement = useMemo(() => {
        return chatId ? <Chat chatId={chatId} promptPreview={true} /> : null;
    }, [chatId]);

    const createButtonsFromConfig = () => {
        return customButtonArray.map((button, index) => {
            if (button.type === 'reset') {
                return (
                    <ResetButton
                        key={index}
                        configKey={button.configKey}
                        prompt={prompt}
                        promptSnapshot={promptSnapshot}
                        PromptActions={PromptActions}
                        customButton={{ variant: button.variant, content: button.text }}
                    />
                );
            } else if (button.type === 'bestPractice') {
                return (
                    <Button
                        key={index}
                        variant={button.variant}
                        onClick={() => PromptActions.updatePrompt({ value: bestPracticeSystemPrompt })}
                    >
                        {button.text}
                    </Button>
                );
            } else {
                return (
                    <Button key={index} variant={button.variant}>
                        {button.text}
                    </Button>
                );
            }
        });
    };

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer classList="!p-0 xl:!py-0">
                <div
                    className={`h-[calc(var(--app-height)-70px)] xl:h-[calc(var(--app-height)-100px)] gap-6 grid grid-cols-12 relative overflow-y-auto p-5 xl:p-10 xl:!pb-0 pb-10 xl:pt-5`}
                >
                    <AdminBox
                        contentLoaded={
                            loadedStates.modelsLoaded && loadedStates.promptLoaded && loadedStates.promptFunctionsLoaded
                        }
                        classString={`bg-transparent p-0 xl:pb-5 mb-0 shadow-none col-span-12 md:col-span-12 xl:col-span-7 2xl:col-span-7 ${
                            isSaving ? 'opacity-50 pointer-events-none' : ''
                        }`}
                    >
                        <div className="flex flex-col gap-6">
                            <AdminBoxHeadline
                                headLine={
                                    promptIdentifier === 'new'
                                        ? i18n.translate({
                                              id: 'title.form.create',
                                              sourceName: 'Prompt',
                                          })
                                        : i18n.translate({
                                              id: 'title.form.edit',
                                              sourceName: 'Prompt',
                                          })
                                }
                                subLine={promptIdentifier === 'new' ? undefined : promptSnapshot.name}
                            />

                            {selectedTab === AssistantTabs.main && (
                                <>
                                    <AdminBox classString={`mb-0 relative`}>
                                        <InfoButton configKey="basicInfo" />
                                        <TabSection
                                            title={i18n.translate({
                                                id: 'tabs.basicInfo.title',
                                                sourceName: 'Prompt',
                                            })}
                                            subline={i18n.translate({
                                                id: 'tabs.basicInfo.subtitle',
                                                sourceName: 'Prompt',
                                            })}
                                        >
                                            {changedSections.basicInfo && (
                                                <div className="absolute top-4 right-12">
                                                    <ResetButton
                                                        configKey="basicInfo"
                                                        prompt={prompt}
                                                        promptSnapshot={promptSnapshot}
                                                        PromptActions={PromptActions}
                                                    />
                                                </div>
                                            )}
                                            <ImageUpload
                                                imagePreview={imagePreview}
                                                handleImageChange={PromptActions.handleImageChange}
                                                camRemoveImage={true}
                                                handleRemoveImage={PromptActions.handleImageRemove}
                                                hasResetOption={changedSections.image}
                                                handleResetImage={() => {
                                                    PromptActions.updatePrompt({ image: promptSnapshot.image });
                                                    PromptActions.setImagePreview(promptSnapshot.image);
                                                }}
                                            >
                                                <Input
                                                    inputType="text"
                                                    value={prompt.name ?? ''}
                                                    className="w-full"
                                                    onChange={(value) => handlePromptUpdate({ name: value })}
                                                    placeholder={i18n.translate({
                                                        id: 'placeholder.name',
                                                        sourceName: 'Prompt',
                                                    })}
                                                />
                                            </ImageUpload>
                                        </TabSection>
                                    </AdminBox>

                                    <AdminBox classString={`mb-0 relative`}>
                                        <InfoButton configKey="systemPrompt" />

                                        <TabSection
                                            title={i18n.translate({ id: 'tabs.prompt.title', sourceName: 'Prompt' })}
                                            subline={i18n.translate({
                                                id: 'tabs.prompt.subtitle',
                                                sourceName: 'Prompt',
                                            })}
                                        >
                                            {prompt.value !== bestPracticeSystemPrompt && (
                                                <div className="absolute top-4 right-12">
                                                    <Button
                                                        variant="transparentRound"
                                                        onClick={() =>
                                                            PromptActions.handleResetSystemPrompt(
                                                                createButtonsFromConfig(),
                                                            )
                                                        }
                                                        tooltipText={i18n.translate({
                                                            id: 'resetbutton.tooltip',
                                                            sourceName: 'Prompt',
                                                        })}
                                                        className="cursor-pointer"
                                                    >
                                                        <ArrowCounterClockwise size={18} />
                                                    </Button>
                                                </div>
                                            )}
                                            <div className="relative">
                                                <TextareaAutosize
                                                    ref={systemPromptInputRef}
                                                    className={`bg-input-backgroundColor w-full p-3 border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100 ${
                                                        !isTextareaFocused
                                                            ? 'overflow-hidden resize-none'
                                                            : 'overflow-auto'
                                                    }`}
                                                    value={prompt.value ?? ''}
                                                    onChange={(e) =>
                                                        isTextareaFocused &&
                                                        handlePromptUpdate({ value: e.target.value })
                                                    }
                                                    onFocus={() => setIsTextareaFocused(true)}
                                                    onBlur={(e) => {
                                                        setIsTextareaFocused(false);
                                                        e.target.scrollTop = 0;
                                                    }}
                                                    minRows={isTextareaFocused ? 5 : 5}
                                                    maxRows={isTextareaFocused ? 15 : 5}
                                                />
                                                {!isTextareaFocused &&
                                                    prompt.value &&
                                                    prompt.value.split('\n').length > 5 && (
                                                        <div className="absolute w-fit h-fit bottom-[-15px] right-0 left-0 m-auto">
                                                            <button
                                                                className="rounded-full h-[25px] md:h-[35px] w-[25px] md:w-[35px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover border-none flex items-center justify-center"
                                                                onClick={() => {
                                                                    systemPromptInputRef.current?.focus();
                                                                }}
                                                            >
                                                                <ArrowDown />
                                                            </button>
                                                        </div>
                                                    )}
                                            </div>
                                        </TabSection>
                                    </AdminBox>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <ConfigBox
                                            title={i18n.translate({
                                                id: 'tabs.model.title',
                                                sourceName: 'Prompt',
                                            })}
                                            description={modelName}
                                            changed={changedSections.model}
                                            targetTab={AssistantTabs.model}
                                            PromptActions={PromptActions}
                                            showReset={changedSections.model}
                                            configKey="model"
                                            prompt={prompt}
                                            promptSnapshot={promptSnapshot}
                                        />

                                        <ConfigBox
                                            title={i18n.translate({
                                                id: 'tabs.connectors.title',
                                                sourceName: 'Prompt',
                                            })}
                                            description={i18n.translate({
                                                id: 'tabs.connector.description',
                                                sourceName: 'Prompt',
                                                params: {
                                                    connectors: prompt.functions.filter(
                                                        (f) => f !== 'code_interpreter' && f !== 'file_search',
                                                    ).length,
                                                },
                                                quantity: prompt.functions.filter(
                                                    (f) => f !== 'code_interpreter' && f !== 'file_search',
                                                ).length,
                                            })}
                                            disabledDescription={i18n.translate({
                                                id: 'tabs.connector.disabledDescription',
                                                sourceName: 'Prompt',
                                                params: {
                                                    model: modelName,
                                                },
                                            })}
                                            changed={PromptActions.hasConnectorChanges()}
                                            disabled={
                                                promptFunctions.filter(
                                                    (f) =>
                                                        f.identifier !== 'code_interpreter' &&
                                                        f.identifier !== 'file_search',
                                                ).length === 0
                                            }
                                            targetTab={AssistantTabs.connector}
                                            showReset={PromptActions.hasConnectorChanges()}
                                            configKey="connector"
                                            prompt={prompt}
                                            promptSnapshot={promptSnapshot}
                                            PromptActions={PromptActions}
                                        />

                                        <ConfigBox
                                            title={i18n.translate({
                                                id: 'tabs.functions.title',
                                                sourceName: 'Prompt',
                                            })}
                                            description={`${i18n.translate({
                                                id: 'tabs.functions.description.functions',
                                                sourceName: 'Prompt',
                                                params: {
                                                    functions:
                                                        prompt.functions.filter((f) => f === 'code_interpreter')
                                                            .length + (prompt.documentProcessing !== 'none' ? 1 : 0),
                                                },
                                                quantity:
                                                    prompt.functions.filter((f) => f === 'code_interpreter').length +
                                                    (prompt.documentProcessing !== 'none' ? 1 : 0),
                                            })}, ${i18n.translate({
                                                id: 'tabs.functions.description.files',
                                                sourceName: 'Prompt',
                                                params: {
                                                    files: prompt.files.length,
                                                },
                                                quantity: prompt.files.length,
                                            })}`}
                                            changed={PromptActions.hasFunctionChanges()}
                                            targetTab={AssistantTabs.functions}
                                            showReset={PromptActions.hasFunctionChanges()}
                                            configKey="functions"
                                            prompt={prompt}
                                            promptSnapshot={promptSnapshot}
                                            PromptActions={PromptActions}
                                        />

                                        <ConfigBox
                                            title={i18n.translate({
                                                id: 'tabs.description.title',
                                                sourceName: 'Prompt',
                                            })}
                                            description={`${i18n.translate({
                                                id: 'tabs.description.description',
                                                sourceName: 'Prompt',
                                                quantity: prompt.description === '' ? 0 : 1,
                                            })} ${i18n.translate({
                                                id: 'tabs.description.examples',
                                                sourceName: 'Prompt',
                                                params: {
                                                    examples: prompt.examples.length,
                                                },
                                                quantity: prompt.examples.length,
                                            })}`}
                                            changed={changedSections.description}
                                            targetTab={AssistantTabs.description}
                                            showReset={changedSections.description}
                                            configKey="description"
                                            prompt={prompt}
                                            promptSnapshot={promptSnapshot}
                                            PromptActions={PromptActions}
                                        />

                                        {!userPrompt && (
                                            <ConfigBox
                                                title={i18n.translate({
                                                    id: 'tabs.knowledgebase.title',
                                                    sourceName: 'Prompt',
                                                })}
                                                description={i18n.translate({
                                                    id:
                                                        knowledgebaseTotal > 0
                                                            ? 'tabs.knowledgebase.description'
                                                            : 'tabs.knowledgebase.noKnowledgebase',
                                                    sourceName: 'Prompt',
                                                    params: {
                                                        knowledgebases: prompt.knowledgebases.length,
                                                    },
                                                    quantity: prompt.knowledgebases.length,
                                                })}
                                                disabledDescription={i18n.translate({
                                                    id: 'tabs.knowledgebase.disabledDescription',
                                                    sourceName: 'Prompt',
                                                    params: {
                                                        model: modelName,
                                                    },
                                                })}
                                                changed={changedSections.knowledgebase}
                                                disabled={knowledgebaseTotal === 0}
                                                targetTab={AssistantTabs.knowledgebase}
                                                showReset={changedSections.knowledgebase}
                                                configKey="knowledgebase"
                                                prompt={prompt}
                                                promptSnapshot={promptSnapshot}
                                                PromptActions={PromptActions}
                                            />
                                        )}

                                        <ConfigBox
                                            title={i18n.translate({
                                                id: 'tabs.dataProtection.title',
                                                sourceName: 'Prompt',
                                            })}
                                            description={i18n.translate({
                                                id:
                                                    prompt.dlp === 'dlp_on'
                                                        ? 'tabs.dataProtection.descriptionOn'
                                                        : prompt.dlp === 'dlp_off'
                                                        ? 'tabs.dataProtection.descriptionOff'
                                                        : 'tabs.dataProtection.descriptionOptional',
                                                sourceName: 'Prompt',
                                            })}
                                            changed={changedSections.dataProtection}
                                            targetTab={AssistantTabs.dataProtection}
                                            showReset={changedSections.dataProtection}
                                            configKey="dataProtection"
                                            prompt={prompt}
                                            promptSnapshot={promptSnapshot}
                                            PromptActions={PromptActions}
                                        />

                                        {!userPrompt && (
                                            <ConfigBox
                                                title={i18n.translate({
                                                    id: 'tabs.groups.title',
                                                    sourceName: 'Prompt',
                                                })}
                                                description={
                                                    groups.length > 0
                                                        ? i18n.translate({
                                                              id: 'tabs.groups.description',
                                                              sourceName: 'Prompt',
                                                              params: {
                                                                  groups: prompt.groups!.length,
                                                              },
                                                              quantity: prompt.groups!.length,
                                                          })
                                                        : i18n.translate({
                                                              id: 'tabs.groups.noGroups',
                                                              sourceName: 'Prompt',
                                                              params: {
                                                                  groups: prompt.groups!.length,
                                                              },
                                                              quantity: prompt.groups!.length,
                                                          })
                                                }
                                                changed={changedSections.groups}
                                                targetTab={AssistantTabs.groups}
                                                showReset={changedSections.groups}
                                                configKey="groups"
                                                prompt={prompt}
                                                promptSnapshot={promptSnapshot}
                                                PromptActions={PromptActions}
                                            />
                                        )}
                                    </div>
                                </>
                            )}

                            {selectedTab !== AssistantTabs.main && (
                                <AdminBox classString={`mb-0 relative`}>
                                    <>
                                        <div className="flex">
                                            <Button
                                                className="absolute top-8 left-8"
                                                variant={'secondaryBigRound'}
                                                onClick={() => PromptActions.closeTabWithoutSaving()}
                                            >
                                                <ArrowLeft />
                                            </Button>

                                            {selectedTab === AssistantTabs.model && (
                                                <ModelTab
                                                    prompt={prompt}
                                                    i18n={i18n}
                                                    handleDropdownChange={PromptActions.handleDropdownChange}
                                                    isUserPrompt={userPrompt}
                                                />
                                            )}
                                            {selectedTab === AssistantTabs.connector && (
                                                <ConnectorTab
                                                    prompt={prompt}
                                                    userPrompt={userPrompt}
                                                    i18n={i18n}
                                                    handlePromptFunctionCheckboxChange={
                                                        PromptActions.handlePromptFunctionCheckboxChange
                                                    }
                                                    PromptActions={PromptActions}
                                                />
                                            )}
                                            {selectedTab === AssistantTabs.functions && (
                                                <FunctionsTab
                                                    PromptActions={PromptActions}
                                                    prompt={prompt}
                                                    models={models}
                                                    userPrompt={userPrompt}
                                                    promptFunctions={promptFunctions}
                                                    i18n={i18n}
                                                    handlePromptFunctionCheckboxChange={
                                                        PromptActions.handlePromptFunctionCheckboxChange
                                                    }
                                                    isAssistantModel={isAssistantModel}
                                                    lastClickedElementRef={lastClickedElementRef}
                                                    filesInputKey={filesInputKey}
                                                    setIsAssistantModel={() => setIsAssistantModel}
                                                    mimeTypes={allowedMimeTypes}
                                                    settings={settings}
                                                />
                                            )}
                                            {selectedTab === AssistantTabs.description && (
                                                <DescriptionTab
                                                    PromptActions={PromptActions}
                                                    prompt={prompt}
                                                    i18n={i18n}
                                                    handlePromptUpdate={handlePromptUpdate}
                                                />
                                            )}
                                            {selectedTab === AssistantTabs.knowledgebase && (
                                                <KnowledgebaseTab
                                                    userPrompt={userPrompt}
                                                    prompt={prompt}
                                                    i18n={i18n}
                                                    PromptActions={PromptActions}
                                                />
                                            )}
                                            {selectedTab === AssistantTabs.dataProtection && (
                                                <DataProtectionTab
                                                    prompt={prompt}
                                                    i18n={i18n}
                                                    handlePromptUpdate={handlePromptUpdate}
                                                    localCombinedDlp={localCombinedDlp}
                                                />
                                            )}
                                            {selectedTab === AssistantTabs.groups && (
                                                <GroupTab
                                                    prompt={prompt}
                                                    i18n={i18n}
                                                    handlePromptUpdate={handlePromptUpdate}
                                                />
                                            )}
                                        </div>
                                    </>
                                </AdminBox>
                            )}
                            <div className="flex gap-2 justify-end">
                                {selectedTab !== AssistantTabs.main && (
                                    <Button variant={'secondary'} onClick={() => PromptActions.closeTabWithSaving()}>
                                        {i18n.translate({
                                            id: 'button.applyChanges',
                                            sourceName: 'Prompt',
                                        })}
                                    </Button>
                                )}
                                <Button
                                    disabled={!hasChanges && !isSaving && prompt.name !== ''}
                                    variant={hasChanges && prompt.name !== '' && !isSaving ? 'primary' : 'disabled'}
                                    onClick={PromptActions.handleSave}
                                >
                                    {promptIdentifier !== 'new'
                                        ? i18n.translate({
                                              id: 'button.saveChanges',
                                              sourceName: 'Prompt',
                                          })
                                        : i18n.translate({
                                              id: 'button.createAssistant',
                                              sourceName: 'Prompt',
                                          })}
                                </Button>
                            </div>
                        </div>
                    </AdminBox>

                    <AdminBox
                        contentLoaded={
                            loadedStates.modelsLoaded && loadedStates.promptLoaded && loadedStates.promptFunctionsLoaded
                        }
                        classString={`${boxHeightClassesStr} !mb-0 xl:sticky top-0 col-span-12 sm:md:col-span-12 md:col-span-12 xl:col-span-5 2xl:col-span-5 overflow-visible hidden lg:block`}
                    >
                        {chatId ? (
                            chatElement
                        ) : (
                            <>
                                {promptIdentifier === 'new' && (
                                    <>
                                        <h4 className="mb-10">
                                            {i18n.translate({ id: 'testAssistant', sourceName: 'Prompt' })}
                                        </h4>
                                        <Badge variant="info">
                                            <div>
                                                <p>
                                                    {i18n.translate({
                                                        id: 'testAssistant.notice',
                                                        sourceName: 'Prompt',
                                                    })}
                                                </p>
                                                <ul className="p mb-0 py-0">
                                                    <li>
                                                        {i18n.translate({ id: 'title.model', sourceName: 'Prompt' })}
                                                    </li>
                                                    <li>
                                                        {i18n.translate({ id: 'title.prompt', sourceName: 'Prompt' })}
                                                    </li>
                                                </ul>
                                            </div>
                                        </Badge>
                                    </>
                                )}
                            </>
                        )}
                    </AdminBox>
                </div>
            </AdminContainer>
        </div>
    );
}

export default Prompt;
